/**
 * The menu hierarchy, walked ONE way — the way the till walks it.
 *
 * 🔴 WHY THIS FILE EXISTS. On 2026-09-21 three walkers disagreed: the item
 * modal followed `parentIds`, while `findOrderablePathsForItem` and the board
 * builder followed `subgroups`. `subgroups` is NOT the hierarchy — it holds a
 * parent's per-child OVERRIDE entries (pricing, visibility, position) and is
 * empty wherever a parent re-prices nothing. At House of Pho every `subgroups`
 * array is empty except one stale entry on "3rd Party Menu" pointing at "Dinner
 * Menu", whose `parentIds` no longer name it. A `subgroups` walk reached almost
 * nothing below a root there, and the one edge it followed is one the till
 * does not show.
 *
 * The POS (`menu_hierarchy_service.dart` `getChildrenForGroup`) takes a group's
 * children as every group whose `parentIds` contain it (or whose legacy
 * `parentId` equals it) and reads `subgroups` only for the per-edge override.
 * This file is that rule. `subgroups` is never a source of children — a stale
 * override entry would pull a group into a menu it has left
 * (docs/BRAND_ARCHITECTURE.md §4).
 *
 * `menuGraphDiagnostics.ts` deliberately reads both shapes to REPORT phantom
 * edges; it is detection, not traversal, and is not built on this.
 *
 * Pure: no Firestore, no React, no clock.
 */
import type { IMenuGroup } from './MenuGroup';
import { SubgroupReference, type ISubgroupReference } from './SubgroupReference';

/** Blunt stop for a pathological nesting; the seen-set already ends cycles. */
export const MAX_MENU_PATH_DEPTH = 12;

/** A group that can appear on a menu: not archived, not deactivated. */
export function isLiveMenuGroup(group: IMenuGroup): boolean {
  return group.isArchived !== true && group.isActive !== false;
}

/** The parents a group names, including the legacy single `parentId`. */
export function menuGroupParentIds(group: IMenuGroup): readonly number[] {
  if (group.parentIds && group.parentIds.length > 0) return group.parentIds;
  const legacy = (group as IMenuGroup & { parentId?: number | null }).parentId;
  return legacy != null ? [legacy] : [];
}

/** Roots: explicit menus when any exist, otherwise parentless groups. */
export function findMenuRoots(groups: readonly IMenuGroup[]): IMenuGroup[] {
  const menus = groups.filter((g) => g.isMenu === true);
  if (menus.length > 0) return menus;
  return groups.filter((g) => menuGroupParentIds(g).length === 0);
}

export interface MenuPathStep {
  readonly group: IMenuGroup;
  /** ROOT→this group, inclusive. */
  readonly path: readonly number[];
  readonly names: readonly string[];
  readonly rootId: number;
  /** The parent's override entry for the edge INTO this group; null at a root or when the parent overrides nothing. */
  readonly edge: ISubgroupReference | null;
}

export interface MenuEdge {
  readonly parent: IMenuGroup;
  readonly child: IMenuGroup;
  readonly rootId: number;
  readonly override: ISubgroupReference | null;
}

export interface WalkMenuPathsOptions {
  /** Which groups exist at all (roots are chosen among these). Default: {@link isLiveMenuGroup}. */
  readonly includeGroup?: (group: IMenuGroup) => boolean;
  /** Per-visit gate, e.g. time/channel availability. Refusing prunes the subtree. */
  readonly enterGroup?: (group: IMenuGroup) => boolean;
  /** Per-edge gate, given the parent's override entry for that edge. */
  readonly enterEdge?: (edge: MenuEdge) => boolean;
  /**
   * Start at these groups instead of {@link findMenuRoots}. A storefront is
   * rooted at the menu it SELECTED (`selectOrderingMenu`), which is a parentless
   * group whether or not it carries `isMenu` — so a walk asked "what can this
   * storefront reach" names its roots rather than letting `isMenu` choose them.
   * An id that is not an included group starts nothing.
   */
  readonly rootIds?: readonly number[];
  readonly maxDepth?: number;
}

export function walkMenuPaths(
  groups: readonly IMenuGroup[],
  visit: (step: MenuPathStep) => void,
  options: WalkMenuPathsOptions = {},
): void {
  const include = options.includeGroup ?? isLiveMenuGroup;
  const maxDepth = options.maxDepth ?? MAX_MENU_PATH_DEPTH;

  const kept = groups.filter((g) => g.menuGroupId != null && include(g));
  const byId = new Map<number, IMenuGroup>();
  for (const g of kept) byId.set(g.menuGroupId as number, g);

  const childrenOf = new Map<number, IMenuGroup[]>();
  for (const g of kept) {
    for (const parentId of menuGroupParentIds(g)) {
      if (parentId === g.menuGroupId || !byId.has(parentId)) continue;
      const list = childrenOf.get(parentId);
      if (!list) childrenOf.set(parentId, [g]);
      else if (!list.includes(g)) list.push(g);
    }
  }

  const descend = (
    group: IMenuGroup,
    path: readonly number[],
    names: readonly string[],
    seen: ReadonlySet<number>,
    rootId: number,
    edge: ISubgroupReference | null,
  ): void => {
    const id = group.menuGroupId as number;
    if (seen.has(id) || path.length >= maxDepth) return;
    if (options.enterGroup && !options.enterGroup(group)) return;

    const nextPath = [...path, id];
    const nextNames = [...names, group.name];
    visit({ group, path: nextPath, names: nextNames, rootId, edge });

    const nextSeen = new Set(seen);
    nextSeen.add(id);
    for (const child of childrenOf.get(id) ?? []) {
      const override =
        SubgroupReference.findForChild(group.subgroups ?? [], child.menuGroupId as number, rootId) ?? null;
      if (options.enterEdge && !options.enterEdge({ parent: group, child, rootId, override })) continue;
      descend(child, nextPath, nextNames, nextSeen, rootId, override);
    }
  };

  const roots = options.rootIds
    ? options.rootIds.flatMap((id) => {
        const root = byId.get(id);
        return root ? [root] : [];
      })
    : findMenuRoots(kept);
  for (const root of roots) {
    descend(root, [], [], new Set(), root.menuGroupId as number, null);
  }
}

export interface ItemMenuPath {
  /** ROOT→LEAF chain — feed straight into `resolveItemPrice`. */
  readonly menuGroupPath: readonly number[];
  /** The leaf group the item hangs in. */
  readonly menuGroupId: number;
  readonly pathNames: readonly string[];
}

/** Every ROOT→LEAF path whose leaf visibly lists `itemId`. Structural: no clock, no channel. */
export function enumerateItemMenuPaths(
  groups: readonly IMenuGroup[],
  itemId: number,
  options?: WalkMenuPathsOptions,
): ItemMenuPath[] {
  const found: ItemMenuPath[] = [];
  walkMenuPaths(
    groups,
    ({ group, path, names }) => {
      const ref = group.items?.find((r) => r.itemId === itemId);
      if (!ref || ref.isHidden === true) return;
      found.push({ menuGroupPath: path, menuGroupId: group.menuGroupId as number, pathNames: names });
    },
    options,
  );
  return found;
}

export interface MenuGroupPath {
  readonly path: readonly number[];
  readonly names: readonly string[];
}

/** Every ROOT→group path, in walk order (a root, then its descendants). */
export function enumerateMenuGroupPaths(
  groups: readonly IMenuGroup[],
  options?: WalkMenuPathsOptions,
): MenuGroupPath[] {
  const found: MenuGroupPath[] = [];
  walkMenuPaths(groups, ({ path, names }) => found.push({ path, names }), options);
  return found;
}
